import { CartDetails, CartItem } from '@domains/cart';
import { Seo } from '@domains/seo';

export type OrderNumber = { order_number: string };

export type OrderActionPayment = { payment_page_url: string };
export type OrderActionSuccess = { success_page_url: string };
export type OrderAction<T extends OrderActionPayment | OrderActionSuccess> = { action: T };

export enum OrderStatusId {
    PendingConfirmation = 'pending_confirmation',
    Confirmed = 'confirmed',
    Cancelled = 'cancelled',
    Closed = 'closed',
}

export type OrderStatus = {
    id: OrderStatusId;
    name: string;
};

export type OrderWholesaleStatus = OrderStatusId.Cancelled | OrderStatusId.Closed | null;

export enum PaymentMethod {
    CardUponReceipt = 'card_upon_receipt',
    CashUponReceipt = 'cash_upon_receipt',
    YooKassa = 'yoo_kassa',
}

export enum PaymentStatus {
    Canceled = 'canceled',
    NotPaid = 'not_paid',
    Succeeded = 'succeeded',
    WaitingForCapture = 'waiting_for_capture',
}

export type NamedPaymentMethod = {
    id: PaymentMethod;
    name: string;
};

export enum ReceivingMethod {
    Courier = 'courier',
    Pickup = 'pickup',
}

export type NamedReceivingMethod = {
    id: ReceivingMethod;
    name: string;
};

export type OrderBase = {
    number: string;
    delivery_type: NamedReceivingMethod;
    delivery_date: FeipApi.DateTime | null;
    delivery_address: string;
    price_products: FeipApi.Kopecks;
    price_delivery: FeipApi.Kopecks;
    price_total: FeipApi.Kopecks;
    remaining_items_count: number;
    preview_images: Global.ImageSet[];
};

type OrderDetails = {
    _actions: ('cancel' | 'repeat')[];
    created_at: FeipApi.DateTime;
    payment_method: NamedPaymentMethod;
    comment: string | null;
    items: CartItem[];
};

// region Order individual
export type OrderIndividualBase = OrderBase & {
    status: OrderStatus;
    payment_status: PaymentStatus | null;
    can_be_paid_online: boolean;
    call_for_confirmation: boolean;
};

// list
export type OrderIndividualListItem = OrderIndividualBase;

export type OrderIndividualList = FeipApi.PaginateList<OrderIndividualListItem>;

export type OrderIndividualListPayload = FeipApi.ListPayload;

// details
export type OrderIndividual = OrderIndividualBase & OrderDetails;

export type OrderIndividualPayload = OrderNumber;
// endregion Order individual

// region Order wholesale
export type OrderWholesaleBase = OrderBase & {
    status: OrderWholesaleStatus;
    establishment_name: string | null;
    establishment_legal_entity: string | null;
    establishment_delivery_address: string | null;
};

// list
export type OrderWholesaleListItem = OrderWholesaleBase;

export type OrderWholesaleList = FeipApi.PaginateList<OrderWholesaleListItem>;

export type OrderWholesaleListPayload = FeipApi.ListPayload & FeipApi.PlatformTypePayload;

// details
export type OrderWholesale = OrderWholesaleBase & OrderDetails;

export type OrderWholesalePayload = OrderNumber & FeipApi.PlatformTypePayload;
// endregion Order wholesale

export type OrdersListPayload = OrderIndividualListPayload | OrderWholesaleListPayload;
export type OrdersListResult = (OrderIndividualList | OrderWholesaleList) & { seo: Seo };

export type OrderDetailsPayload = OrderIndividualPayload | OrderWholesalePayload;
export type OrderDetailsResult = OrderIndividual | OrderWholesale;

export type OrderPayPayloadData = OrderNumber;
export type OrderPayResultData = OrderAction<OrderActionPayment | OrderActionSuccess>;

export type OrderCancelPayloadData = OrderNumber;

export type OrderSuccessPayloadData = OrderNumber;
export type OrderSuccessResultData = {
    number: string;
    delivery_type: NamedReceivingMethod;
    delivery_date: FeipApi.DateTime;
    delivery_address: string;
    price_products: number;
    price_total: number;
    call_for_confirmation: boolean;
    payment_method?: NamedPaymentMethod;
    payment_status?: PaymentStatus;
};

export type OrderRepeatPayloadData = OrderNumber;
export type OrderRepeatResultData = CartDetails;

export type OrderCheckUnavailableItemsPayloadData = OrderNumber;
export type OrderCheckUnavailableItemsResultData = FeipApi.ListResult<CartItem>;
